/**
 * @file spy日志
 */

import {env} from '@baidu/boxx-fe';
import {getNetworkStatus} from '../utils/native';
import {getKeyCommonParams} from '../utils/tools';

const ONLINE_HOST_MAP: {
    [key: string]: boolean;
} = {
    'yiyanapp.baidu.com': true,
    'wanhua.baidu.com': true,
    // 'sefe-dingkaiqiang.bcc-gzbh.baidu.com:8081': true,
};
const errorStackCallbackArrays = [] as Array<{group: string; e: Error; metric: Record<string, unknown>}>;
const countStackCallbackArrays = [] as Array<{group: string; metric: Record<string, number>}>;
const distStackCallbackArrays = [] as
    Array<{group: string; metric: Record<string, unknown>; dim?: Record<string, unknown>}>;
const perfStackCallbackArrays = [] as
    Array<{group: string; metric: Record<string, unknown>; dim?: Record<string, unknown>}>;
let isSpyInit = false;
const performanceDataMap: Map<string, Record<string, unknown>> = new Map();

// 处理ab实验数据
const dealSid = () => {
    // aladdin实验
    const arr = [
        '125507_1',
        '125507_2',
    ];
    const sids = getKeyCommonParams('sid')?.split('-');
    return sids.find(item => arr.includes(item)) || '';
};

const getDim = () => {
    const {isAndroid, isIOS, boxVersion} = env;
    return {
        os: (isAndroid && 'android')
            || (isIOS && 'ios')
            || 'unknown',
        netType: (window?.navigator?.connection?.effectiveType) || 'unknown',
        downlinkMax: (window?.navigator?.connection?.downlinkMax) || 'unknown',
        host: window.location.href,
        boxVersion,
        sid: dealSid(),
        hostTag: window.globalData?.hostTag || 'unknown',
    };
};

const qualify = () => {
    return ONLINE_HOST_MAP[location.host];
};

/**
 * appendScript
 * 在Body标签后面插入一段js脚本
 * @param params script标签的变量
 * @example
 * appendScript({src: 'http://xxx.js'})
 */
export const appendScript = (params: Record<string, unknown>) => {
    if (!params) {
        return;
    }
    const script = document.createElement('script') as HTMLScriptElement & Record<string, unknown>;
    script.type = 'text/javascript';
    // eslint-disable-next-line no-unused-vars
    for (const i in params) {
        if (params.hasOwnProperty(i)) {
            script[i] = params[i];
        }
    }
    document.body.appendChild(script);
};

/**
 * spy性能打点封装
 *
 * @param group spy上配置的分组名称
 * @param metric spy上的【指标组】所定义的scheme的字段信息 对应info字段
 * @param dim spy上【纬度】所定义的scheme字段信息，对应dim字段，已经默认取了os-系统信息，netType-网络类型，downlinkMax-基础连接技术的最大下行网络速度
 */
export const perfLog = (group: string, metric: Record<string, unknown>, dim: Record<string, unknown> = {}) => {
    if (!qualify()) {
        return;
    }
    if (!isSpyInit) {
        perfStackCallbackArrays.push({
            group,
            metric,
            dim,
        });
        return;
    }
    const params = {
        pid: '10_149',
        dim: {
            ...getDim(),
            ...dim,
        },
        type: 'perf',
        group,
        info: metric,
    };
    window.__spyClient?.sendPerf(params);
};

/**
 * spy异常打点封装 errorLog
 * spy错误监控，调用后若spy没有init，会等待其init后一起调用
 * @param group spy上配置的分组名称
 * @param e 错误信息 可以通过 new Error('too large') 构造
 * @param metric spy上的【指标组】所定义的scheme的字段信息 对应info字段
 * dim字段暂时为默认的 已经默认取了os-系统信息，netType-网络类型，downlinkMax-基础连接技术的最大下行网络速度
 */
export const errorLog = async (group = 'im_error', e: Error | string, metric: Record<string, unknown> = {}) => {
    // spy数据量过大，hybrid先过滤'Script error.' 错误类型，
    if (!qualify() || e instanceof Error && e.message === 'Script error.') {
        return;
    }
    if (typeof e === 'string') {
        const {networkType, isWeakNet} = await getNetworkStatus();
        e = new Error(`${e}, net=${networkType}, isWeakNet=${isWeakNet}`);
    }
    if (!isSpyInit) {
        errorStackCallbackArrays.push({
            group,
            e,
            metric,
        });
        return;
    }
    const params = {
        pid: '10_149',
        dim: getDim(),
        group,
        info: metric,
    };
    window.__spyClient?.sendExceptForError(e, params);
};

export const countLog = (group: string, metric: Record<string, number> = {}) => {
    if (!qualify()) {
        return;
    }
    if (!isSpyInit) {
        countStackCallbackArrays.push({
            group,
            metric,
        });
        return;
    }
    const params = {
        pid: '10_149',
        dim: getDim(),
        group,
        info: metric,
    };
    window.__spyClient?.sendCount(params);
};

export const distLog = (group: string, metric: Record<string, unknown> = {}, dim: Record<string, unknown> = {}) => {
    if (!qualify()) {
        return;
    }
    if (!isSpyInit) {
        distStackCallbackArrays.push({
            group,
            metric,
        });
        return;
    }
    const params = {
        pid: '10_149',
        dim: {
            ...getDim(),
            ...dim,
        },
        group,
        info: metric,
    };
    window.__spyClient?.sendDist(params);
};

/**
 * initSpy
 * 初始化spy脚本，目前主要用于错误监控
 */
export const initSpy = (group: string) => {
    if (!qualify() || !group) {
        return;
    }
    if (isSpyInit && !window.__spyClient) {
        // eslint-disable-next-line no-undef
        window.__spyClient = new SpyClient({
            pid: '10_149',
            sample: 1,
            check: false,
        });
        return;
    }
    appendScript({
        src: 'https://code.bdstatic.com/npm/spy-client@2.1.8/dist/spy-client.min.js?clientprefetch=1',
        onload: () => {
            const sendPerf = (info: Record<string, unknown>) => {
                perfLog(group, info);
            };
            // eslint-disable-next-line no-undef
            window.__spyClient = new SpyClient({
                pid: '10_149',
                sample: 1,
                check: false,
            });
            isSpyInit = true;
            // tti
            window.__spyClient.listenTTI(sendPerf);

            // 先获取spy的常规参数
            window.__spyClient.listenTiming(sendPerf);
            errorStackCallbackArrays.forEach(({group, e, metric}) => {
                errorLog(group, e, metric);
            });
            errorStackCallbackArrays.length = 0;

            countStackCallbackArrays.forEach(({group, metric}) => {
                countLog(group, metric);
            });
            countStackCallbackArrays.length = 0;

            distStackCallbackArrays.forEach(({group, metric, dim = {}}) => {
                distLog(group, metric, dim);
            });
            distStackCallbackArrays.length = 0;

            perfStackCallbackArrays.forEach(({group, metric, dim = {}}) => {
                perfLog(group, metric, dim);
            });
            perfStackCallbackArrays.length = 0;
        },
    });
};

// 性能打点
export const performanceLog = (params: Record<string, unknown>) => {
    const startTime = window.performance.timing.navigationStart;
    const {group = 'chat', send = false, ...metrics} = params;
    // eslint-disable-next-line guard-for-in
    for (const index in metrics) {
        metrics[index] = metrics[index] as number - startTime;
    }

    const arr = performanceDataMap.has(group as string) ? performanceDataMap.get(group as string) : {};
    // 先更新对应的数据
    performanceDataMap.set(group as string, {
        ...arr,
        ...metrics,
    });

    // 单发当前group
    if (send) {
        perfLog(group as string, performanceDataMap.get(group as string) || {});
        performanceDataMap.delete(group as string);
    }

    // 全部group都发
    // if (send) {
    //     performanceDataMap.forEach((value, key) => {
    //         if (key === type) {
    //             perfLog(key, value);
    //         }
    //     });
    //     performanceDataMap.clear();
    // }
};
